import { Box, Grid, Button, Container, TextField, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAppUtils from '../../utils/appUtils';
import { validateZipCodes, parseCsv } from '../../utils/parseCSV';
import * as _ from 'lodash';

interface Iprops {
  zipcodes: string;
  hasError: boolean;
  onChange: (newItems: string) => void;
}

const FuelGeofencingZipcodes = ({ zipcodes = '', hasError = false, onChange = null }: IProps) => {
  const [myzipcodes, setMyZipCodes] = useState<string>(zipcodes);
  const zipcodeInput = useRef(null);
  const fileInput = useRef(null);
  const [csvFile, setCsvFile] = useState<File>(null);
  const { confirm, alert } = useAppUtils();

  useEffect(() => {
    // console.log('zipcodes changed', { zipcodes });
    setInputValue(zipcodes);
  }, [zipcodes]);

  const getInputValue = () => {
    const input: HTMLElement | null = zipcodeInput.current;
    if (input) {
      const inputInners = input.getElementsByTagName('textarea');
      if (inputInners && inputInners.length > 0) {
        return inputInners[0].value;
      }
    }
    return null;
  };

  const setInputValue = (val: string) => {
    const input: HTMLElement | null = zipcodeInput.current;
    if (input) {
      const inputInners = input.getElementsByTagName('textarea');
      if (inputInners && inputInners.length > 0) {
        inputInners[0].value = val;
      }
    }
  };

  const onBrowseButtonClick = () => {
    // `current` points to the mounted file input element
    fileInput.current.click();
  };

  const onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const newCSV = cleanseCSV(value);
    setInputValue(newCSV);
    onChange(newCSV);
  };

  const cleanseCSV = (csv: string) => {
    if (csv == null) return null;
    return _.uniq(csv.split(',').map((s) => s.trim())).join(', '); // give the user formatted output...
  };

  const loadZipCodesFromFile = (event) => {
    const file = event.target.files[0];
    const filename = file.name;
    const fileExt = filename.substring(filename.lastIndexOf('.'));
    if (fileExt !== '.csv') {
      alert(`Failed to import file: ${filename} - file type is incorrect. Please check the file type.`, 'error');
      // this.errorMessage = `Failed to import file: ${filename} - file type is incorrect. Please check the file type.`;
      return;
    }
    const reader = new FileReader();

    reader.onload = (e) => {
      const raw = e.target.result;
      if (typeof raw !== 'string') throw `Unexpected! e.target.result was not a string!`;

      parseCsv(raw)
        .then((zipCodes) => {
          let currentZips = getInputValue();
          if (currentZips == null) {
            currentZips = '';
          }
          const newZipCodes = [currentZips, zipCodes].filter((s) => s.trim().length !== 0).join(', ');
          const error: any = validateZipCodes(newZipCodes);
          if (error != null) {
            alert(error.message, 'error');
          } else {
            const normalized = _.uniq(newZipCodes.split(',').map((s) => s.trim())).join(', '); // give the user formatted output...
            setInputValue(normalized);
            onChange(normalized);
          }
        })
        .catch((err) => {
          alert(`Failed to import file: ${filename} - data format is incorrect. Please check the file.`, 'error');
        });
    };
    reader.readAsText(file);
    event.target.value = null;
    return false;
  };

  return (
    <Container className="fuel-geofencing-zipcodes" sx={{ pt: 1 }}>
      <Grid container className="section-box" spacing={2}>
        <Grid item xs={2}>
          Allowed Zip Codes:
        </Grid>
        <Grid item xs={8}>
          <TextField
            ref={zipcodeInput}
            variant="outlined"
            fullWidth
            label=""
            placeholder="Enter zip codes"
            multiline
            rows={4}
            onBlur={onBlur}
            // onChange={handleChange}
            error={hasError}
            required
          />
        </Grid>
        <Grid item xs={2}>
          <Grid item xs={2}>
            <input className="hidden" type="file" id="file" ref={fileInput} onChange={loadZipCodesFromFile} />
            <Button className="import-csv-button" color="primary" variant="contained" onClick={onBrowseButtonClick}>
              Import CSV
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ fontSize: '14px', lineHeight: 1.25 }}>
            FUEL supports both Allowed listing of Zip Codes and Blocked listing of States.
            <p>&nbsp;</p>
            <Typography sx={{ textDecoration: 'underline' }}>Allowed Zip Code </Typography>
            To Include/Allow zip codes that will have access to the content, if the viewer is identified to be within that state,
            enter the zip codes separated by a comma.
            <p>&nbsp;</p>
            EXAMPLE: 92660,92661,92662,92663,92664,92665,92666,92667
            <p>&nbsp;</p>
            <Typography sx={{ textDecoration: 'underline' }}>Blocked list by State </Typography>
            To Exclude/block states that will not have access to the content, if the viewer is identified to be within that state,
            enter: ExcludeRegions, + the state 2 letter abbreviation separated by commas
            <p>&nbsp;</p>
            <p>EXAMPLE: ExcludeRegions,AL,AK,AR,CT,DE,FL,GA</p>
            <p>&nbsp;</p>
            <p>Additional format notes: </p>
            <p>&nbsp;</p>
            <Box className="list-box">
              <ul>
                <li>The text format of capital ‘E’ and ‘R’ is Required</li>
                <li>The ‘comma’ after ExcludeRegions is Required</li>
                <li>The state abbreviations are NOT case sensitive (i.e. both CA or ca will work)</li>
                <li>Commas after the state abbreviations is Required</li>
              </ul>
            </Box>
            <p>&nbsp;</p>
            IMPORTANT: Combining allowed listing of Zip Codes{' '}
            <Typography sx={{ textDecoration: 'underline', display: 'inline' }}>and</Typography> blocked listing of States is{' '}
            <Typography sx={{ textDecoration: 'underline', display: 'inline' }}>NOT</Typography> supported
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default FuelGeofencingZipcodes;
